// Per-engine native-JSON type detection.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice A2.
//
// Returns a `JsonClassification` for column data types the engine recognises as
// natively JSON, or `undefined` when the type is not engine-native JSON. The
// caller (`classifyJsonColumn`) cascades to CHECK-constraint detection (A3),
// MetaInfo declaration (A4), and inference (C4) when this returns `undefined`.
//
// Per-engine matrix (JCS-2 precedence: engine-native first):
// - postgres: `jsonb` → native-jsonb; `json` → native-json.
// - mysql:    `json` → native-json. (MySQL `JSON` is binary-internal but
//             presents lower-cased in INFORMATION_SCHEMA.COLUMNS.DATA_TYPE.)
// - oracle:   `json` (21c+) → native-json. Oracle's pre-21c JSON-on-CLOB/BLOB
//             with `IS JSON` constraint is A3 territory (check-isjson).
// - mssql:    no native JSON type — every column returns `undefined` (CHECK
//             ISJSON detection is A3).
// - sqlite:   no native JSON type — every column returns `undefined` (sqlite
//             stores `JSON`-declared columns as BLOB; CHECK json_valid
//             detection is A3).
//
// Comparison is case-insensitive — schema-cache adapters report data types in
// engine-native casing (lowercase for pg/mysql/sqlite, uppercase for Oracle in
// most cases) and we want to be tolerant of either form.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { JsonClassification } from '../../types';

const NATIVE_JSONB: JsonClassification = { kind: 'native-jsonb' };
const NATIVE_JSON: JsonClassification = { kind: 'native-json' };

export function classifyNativeJsonType(
  dataType: string,
  engine: SqlEngine,
): JsonClassification | undefined {
  const normalized = dataType.trim().toLowerCase();
  if (normalized === '') return undefined;
  switch (engine) {
    case 'postgres':
      if (normalized === 'jsonb') return NATIVE_JSONB;
      if (normalized === 'json') return NATIVE_JSON;
      return undefined;
    case 'mysql':
      if (normalized === 'json') return NATIVE_JSON;
      return undefined;
    case 'oracle':
      if (normalized === 'json') return NATIVE_JSON;
      return undefined;
    // TODO(v2): SQL Server 2025 ships a native 'json' type — classify it here
    // when supported (PRD-deferred to v2 per JCS § "No scope creep").
    case 'mssql':
    case 'sqlite':
      return undefined;
    default:
      return undefined;
  }
}
